import React from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "ui/routes";
import createPoller from "utils/createPoller";
import { Button } from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import { requestVerification } from "core/services/site/user";
import styles from "./NotVerified.module.scss";

const NotVerified: React.FC = () => {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const [requested, setRequested] = React.useState<boolean>(false);

    const handleRequestVerification = async () => {
        if (!sessionContext.session) {
            return;
        }
        const result = await requestVerification(sessionContext.session.token);
        if (result.error) {
            throw Error(result.error);
        }
        setRequested(true);
    };

    const pollVerification = async () => {
        let failureCount = 0;
        await createPoller(async () => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                const isVerified = sessionContext.session.user.verified;
                if (!isVerified) {
                    return false;
                }
                navigate(paths.projectsPage);
                return true;
            } catch (err) {
                failureCount += 1;
                if (failureCount > 4) {
                    // Assume this fragment is broken and stop polling
                    return true;
                }
                return false;
            }
        }, [{ iterations: 5000, interval: 3000 }]);
    };

    React.useEffect(() => {
        pollVerification();
    }, []);

    return (
        <div className={styles.page}>
            <div className={styles.notverified}>
                <div className={styles.notverified__heading}>
                    This is strange ¯\_(ツ)_/¯
                </div>
                <div className={styles.notverified__text}>
                    Looks like you are not verified yet, please check your email
                    and verify your account.
                </div>
                <div className={styles.notverified__actions}>
                    {!requested && (
                        <Button
                            type="secondary"
                            theme="light"
                            onClick={handleRequestVerification}
                        >
                            Resend email
                        </Button>
                    )}
                </div>
                {requested && (
                    <div className={styles.notverified__confirm}>
                        We&apos;ve sent you a new verification email. Please
                        check your inbox.
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotVerified;
